
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LazyChartLoader, ChartCard } from "@/components/ui/lazy-chart";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { ErrorBoundary } from "@/components/ui/error-boundary";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

interface DataPoint {
  name: string;
  [key: string]: any;
}

interface UserTrendChartProps {
  data: DataPoint[];
  isLoading: boolean;
}

export const UserTrendChart = ({ data, isLoading }: UserTrendChartProps) => {
  if (isLoading) {
    return <div className="flex items-center justify-center h-full">
      <Skeleton className="h-64 w-full" />
    </div>;
  }

  return (
    <LazyChartLoader>
      {(recharts) => {
        const { ResponsiveContainer, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, Area } = recharts;
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="Users" stroke="#8884d8" fill="#8884d8" />
            </AreaChart>
          </ResponsiveContainer>
        );
      }}
    </LazyChartLoader>
  );
};

interface BookingTrendChartProps {
  data: DataPoint[];
  isLoading: boolean;
}

export const BookingTrendChart = ({ data, isLoading }: BookingTrendChartProps) => {
  if (isLoading) {
    return <div className="flex items-center justify-center h-full">
      <Skeleton className="h-64 w-full" />
    </div>;
  }

  return (
    <LazyChartLoader>
      {(recharts) => {
        const { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar } = recharts;
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="Bookings" fill="#4f46e5" />
            </BarChart>
          </ResponsiveContainer>
        );
      }}
    </LazyChartLoader>
  );
};

interface EngagementChartProps {
  data: DataPoint[];
  isLoading: boolean;
}

export const EngagementChart = ({ data, isLoading }: EngagementChartProps) => {
  if (isLoading) {
    return <div className="flex items-center justify-center h-full">
      <Skeleton className="h-64 w-full" />
    </div>;
  }

  return (
    <ErrorBoundary componentName="EngagementChart">
      <ChartContainer
        config={{
          Reviews: { color: '#f59e0b' },
          Favorites: { color: '#ec4899' }
        }}
        className="h-96"
      >
        <LazyChartLoader>
          {(recharts) => {
            const { BarChart, CartesianGrid, XAxis, YAxis, Legend, Bar } = recharts;
            const ChartTooltip = recharts.Tooltip;
            return (
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Bar dataKey="Reviews" fill="var(--color-Reviews)" />
                <Bar dataKey="Favorites" fill="var(--color-Favorites)" />
              </BarChart>
            );
          }}
        </LazyChartLoader>
      </ChartContainer>
    </ErrorBoundary>
  );
};

interface PieChartCardProps {
  data: { name: string; value: number }[];
  isLoading: boolean;
  colors?: string[];
}

export const PieChartCard = ({ data, isLoading, colors = COLORS }: PieChartCardProps) => {
  if (isLoading) {
    return <div className="flex items-center justify-center h-full">
      <Skeleton className="h-64 w-full" />
    </div>;
  }

  return (
    <LazyChartLoader>
      {(recharts) => {
        const { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } = recharts;
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      }}
    </LazyChartLoader>
  );
};

interface ServicePopularityChartProps {
  data: { name: string; value: number }[];
  isLoading: boolean;
}

export const ServicePopularityChart = ({ data, isLoading }: ServicePopularityChartProps) => {
  if (isLoading) {
    return <div className="flex items-center justify-center h-full">
      <Skeleton className="h-64 w-full" />
    </div>;
  }

  return (
    <LazyChartLoader>
      {(recharts) => {
        const { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar } = recharts;
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        );
      }}
    </LazyChartLoader>
  );
};

// Re-export the ChartCard from lazy-chart for convenience
export { ChartCard };
